// src/pages/Ads/AdsListPage.jsx
import "./AdsListPage.scss";

import React, { useState } from "react";

import AppLinkButton from "../../../commons/buttons/AppLinkButton";
import AppLoading from "../../../commons/AppLoading";
import CalendarInput from "../../../commons/inputs/CalendarInput";
import ConfirmModal from "../../../commons/modal/ConfirmModal";
import EmptyState from "../../../commons/EmptyState";
import FloatingVideoButton from "../../../commons/buttons/FloatingVideoButton";
import HallAppToast from "../../../commons/HallAppToast";
import InformativeModal from "./components/InformativeModal";
import MenuTitle from "../../../commons/menu/MenuTitle";
import MetaAPI from "../MetaAPI";
import PullToRefresh from "react-simple-pull-to-refresh";
import { formatInReal } from "../../../commons/util/HallAppUtil";
import { useNavigate } from "react-router-dom";

const metaAPI = new MetaAPI();

export default function AdsListPage() {
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [metaLoading, setMetaLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showModalDelete, setShowModalDelete] = useState(false);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState({});
  const [isLoadingModal, setIsLoadingModal] = useState(false);
  const [showModalActive, setShowModalActive] = useState(false);

  // date range state compatible com CalendarInput
  const [dates, setDates] = useState([new Date(), new Date()]);
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [previewHtml, setPreviewHtml] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showMetricsInfo, setShowMetricsInfo] = useState(false);

  function changeDates(start, end) {
    setStartDate(start);
    setEndDate(end);
    // setDates([start, end]);
  }

  const toIsoDate = (d) => {
    if (!d) return null;
    const date = d instanceof Date ? d : new Date(d);
    if (isNaN(date)) return null;
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const buildMetricsInfo = () => {
    return (
      <div className="p-3 border rounded bg-light">
        <h5 className="fw-bold">📊 O que significam essas métricas?</h5>

        <div className="mt-3">
          <h6 className="fw-bold">👀 Impressões</h6>
          <p>
            “Impressões” representam quantas vezes seu anúncio <strong>foi exibido</strong> na tela das pessoas.
          </p>
          <ul>
            <li>Não significa que alguém clicou.</li>
            <li>Se uma pessoa viu 3 vezes, conta como 3 impressões.</li>
            <li>Aumentar as impressões ajuda a sua marca a ser mais vista.</li>
          </ul>
        </div>

        <div className="mt-3">
          <h6 className="fw-bold">🖱️ Cliques</h6>
          <p>
            São os momentos em que alguém <strong>tocou ou clicou</strong> no seu anúncio.
          </p>
          <ul>
            <li>Clique pode abrir o WhatsApp, o Instagram, o site ou o post.</li>
            <li>Quanto mais cliques, mais pessoas demonstraram interesse real.</li>
          </ul>
        </div>

        <div className="mt-3">
          <h6 className="fw-bold">📈 CTR (Taxa de Cliques)</h6>
          <p>
            É a porcentagem de pessoas que <strong>clicaram</strong> depois de verem o anúncio.
          </p>
          <p>
            A fórmula é simples: <strong>(Cliques ÷ Impressões) × 100</strong>
          </p>
          <ul>
            <li>CTR alto = anúncio interessante.</li>
            <li>CTR baixo = anúncio não chamou atenção.</li>
            <li>Entre 1% e 3% é considerado bom para pequenas empresas.</li>
          </ul>
        </div>

        <div className="alert alert-info mt-3 mb-0" style={{ fontSize: "14px" }}>
          💡 <strong>Dica:</strong> se o CTR estiver baixo, tente melhorar a imagem, texto ou chamada para ação.
        </div>
      </div>
    );
  };


  const fetchAds = async () => {
    setError(null);

    const isoStart = toIsoDate(startDate);
    const isoEnd = toIsoDate(endDate);

    if (!isoStart || !isoEnd) {
      setError("Intervalo de datas inválido.");
      return;
    }
    if (isoStart > isoEnd) {
      setError("Data de início não pode ser posterior à data de término.");
      return;
    }

    try {
      setLoading(true);
      const response = await metaAPI.getAnnouncements(isoStart, isoEnd);
      setAds(response.data || []);
    } catch (err) {
      console.error("Erro ao buscar anúncios:", err);
      setError("Erro ao carregar anúncios.");
      setAds([])
      HallAppToast.genericError();
    } finally {
      setLoading(false);
    }
  };

  const navigate = useNavigate();

  const handleEdit = (ad) => {
    const data = {
      ...ad,
    }
    navigate('/marketing/editar-anuncio', {
      state: { ad: data }
    });
  };

  const handleDelete = async (data) => {
    setSelectedAnnouncement(data)
    setShowModalDelete(true)
    setIsLoadingModal(false)
  };

  const handleView = async (data) => {
    try {
      setLoading(true);
      const response = await metaAPI.getPreviews(data.internalId);

      const html = response.data;

      if (html) {
        setPreviewHtml(html);
        setShowPreview(true);
      } else {
        HallAppToast.error("Não foi possível carregar o preview do anúncio.");
      }

    } catch (err) {
      console.error("Erro ao ver preview do anúncio:", err);
      HallAppToast.genericError();
    } finally {
      setLoading(false);
    }
  };

  const handleActive = async (data) => {
    setSelectedAnnouncement(data)
    setShowModalActive(true)
    setIsLoadingModal(false)
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const d = new Date(dateStr);
    if (!isNaN(d)) return d.toLocaleDateString("pt-BR");
    return dateStr;
  };

  const toNumberSafe = (v) => {
    if (v == null || v === "") return 0;
    if (typeof v === "number") return v;
    const n = parseFloat(String(v).replace(",", "."));
    return isNaN(n) ? 0 : n;
  };

  const genderLabel = (gender) => {
    if (gender === 'female') return 'Mulheres'
    if (gender === 'male') return 'Homens'
    if (gender === 'all') return 'Todos'

  };

  const getStatusClass = (status) => {
    switch (status) {
      case "ACTIVE":
        return "status-active";
      case "PAUSED":
      case "CAMPAIGN_PAUSED":
        return "status-paused";
      default:
        return "status-default";
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "ACTIVE":
        return "Ativo";

      case "PAUSED":
      case "CAMPAIGN_PAUSED":
      case "ADSET_PAUSED":
        return "Pausado";

      case "PENDING_REVIEW":
        return "Aguardando análise";

      case "IN_PROCESS":
        return "Em análise";

      case "PREAPPROVED":
        return "Pré-aprovado";

      case "DISAPPROVED":
        return "Reprovado";

      case "WITH_ISSUES":
        return "Com problemas";

      case "NOT_DELIVERING":
        return "Sem entrega";

      case "ARCHIVED":
        return "Arquivado";

      default:
        return "Indefinido";
    }
  };
  const extractTargeting = (targetingWrapper) => {
    if (!targetingWrapper) return {};
    const inner = targetingWrapper.targeting || targetingWrapper;
    if (!inner || typeof inner !== "object") return {};
    const interests = inner.interests;
    const locations = inner.locations;
    const ageMin = inner.ageMin || inner.age_min || null;
    const ageMax = inner.ageMax || inner.age_max || null;
    const gender = inner.gender || null;
    const targetingAutomation = inner.targetingAutomation || inner.targeting_automation || null;
    const advantageAudience = targetingAutomation?.advantageAudience ?? targetingAutomation?.advantage_audience ?? null;
    const individualSetting = targetingAutomation?.individualSetting || targetingAutomation?.individual_setting || null;

    return {
      interests,
      locations,
      ageMin,
      ageMax,
      gender,
      advantageAudience,
      individualSetting,
    };
  };


  async function executeDeletion() {
    try {
      setIsLoadingModal(true)
      await metaAPI.deleteAnnouncements(selectedAnnouncement?.internalId)
      HallAppToast.success()
      await fetchAds()
    } catch (_) {
      HallAppToast.error()
    } finally {
      setIsLoadingModal(true)
      setSelectedAnnouncement({})
      setShowModalDelete(false)
    }
  }

  async function executeActiveOrPause() {
    try {
      setIsLoadingModal(true)
      const status = selectedAnnouncement?.status === "ACTIVE" ? 'PAUSED' : 'ACTIVE'
      await metaAPI.updateAnnouncementStatus(selectedAnnouncement?.internalId, status)
      await fetchAds()
      HallAppToast.success()
    } catch (_) {
      HallAppToast.genericError()
    } finally {
      setIsLoadingModal(true)
      setSelectedAnnouncement({})
      setShowModalActive(false)
    }
  }


  return (
    <div className="ads-list-page">
      <div className="contaicsner">
        <MenuTitle title="Meus Anúncios" showArrowInfo={false} />

        <PullToRefresh onRefresh={async () => { await fetchAds(); }}>
          <div className="ads-controls">
            <div className="select-row">

              {
                showModalActive || showModalDelete || showPreview || showMetricsInfo ? <></> : <div className="calendar-wrapper">
                  <label className="select-label">
                    Período de exibiçãos <span className="required">*</span>
                  </label>
                  <CalendarInput
                    className="hallapp-calendar"
                    inputClassName="hallapp-calendar-input"
                    setDates={setDates}
                    changeDates={changeDates}
                    values={dates}
                    inputStyle={{ height: '52px' }}
                  />
                  <small className="input-info">
                    Selecione o intervalo de datas para visualizar as métricas dos anúncios.
                  </small>
                </div>
              }

              <div className="actions-row" style={{ marginTop: '8px' }}>
                <button
                  className="btn btn-primary"
                  onClick={fetchAds}
                  disabled={loading || metaLoading}
                  aria-label="Pesquisar anúncios"
                >
                  {loading ? "Carregando..." : "Pesquisar"}
                </button>
              </div>
            </div>
          </div>

          {error && <div className="ads-error">{error}</div>}
          <hr></hr>

          <div className="ads-grid">
            {metaLoading ? (
              <AppLoading />
            ) : !loading && ads.length === 0 ? (
              <EmptyState
                title="Nenhum anúncio encontrado"
                description="Não encontramos anúncios no período selecionado. Ajuste a conta ou o intervalo de datas."
                actionLabel="Pesquisar novamente"
                onAction={fetchAds}
              />
            ) : loading ? (
              <AppLoading />
            ) : (
              ads.map((ad) => {
                const metricsSource =
                  ad.metrics && typeof ad.metrics === "object" && (ad.metrics.impressions || ad.metrics.clicks)
                    ? ad.metrics
                    : ad.interests && typeof ad.interests === "object" && ad.interests.impressions
                      ? ad.interests
                      : null;

                const impressions = toNumberSafe(metricsSource?.impressions);
                const clicks = toNumberSafe(metricsSource?.clicks);
                const ctr = impressions ? ((clicks / impressions) * 100).toFixed(2) + "%" : "-";

                const targetingData = extractTargeting(ad.targeting);
                const interestsFromTargeting = targetingData.interests || [];
                const locationsFromTargeting = targetingData.locations || [];
                const ageMin = targetingData.ageMin;
                const ageMax = targetingData.ageMax;
                const gender = targetingData.gender;
                const advantageAudience = targetingData.advantageAudience;

                const interestsToShow =
                  Array.isArray(interestsFromTargeting) && interestsFromTargeting.length > 0
                    ? interestsFromTargeting
                    : Array.isArray(ad.interests)
                      ? ad.interests
                      : null;

                const locationsToShow =
                  Array.isArray(locationsFromTargeting) && locationsFromTargeting.length > 0
                    ? locationsFromTargeting
                    : Array.isArray(ad.locations)
                      ? ad.locations
                      : null;

                const startTime = ad.startTime || (metricsSource?.dateStart || "") || "";
                const endTime = ad.endTime || (metricsSource?.dateStop || "") || "";

                return (
                  <article className="ad-card" key={ad.id} role="article" aria-labelledby={`ad-title-${ad.id}`}>
                    <header className="card-header">
                      <h3 id={`ad-title-${ad.id}`} className="card-title">
                        {ad.name || "-"}
                      </h3>
                      <span className={`card-status ${getStatusClass(ad.status)}`}>
                        {getStatusLabel(ad.status)}
                      </span>
                    </header>

                    <div className="card-body">
                      <div className="card-section">
                        <p className="muted">
                          <strong>Período:</strong> {formatDate(startTime)} até {formatDate(endTime)}
                        </p>
                        <p className="muted">
                          <strong>Orçamento diário:</strong> {formatInReal(ad.dailyBudget)}
                        </p>
                        {(ageMin || ageMax || gender || advantageAudience != null) && (
                          <p className="muted">
                            <strong>Público:</strong>{" "}
                            {ageMin || ageMax ? `${ageMin ?? "-"}–${ageMax ?? "-"}` : null}
                            {gender ? ` • ${genderLabel(gender)}` : null}
                            {advantageAudience != null ? ` • Advantage: ${advantageAudience === 1 ? "Ativo" : "Inativo"}` : null}
                          </p>
                        )}
                      </div>

                      <div style={{ display: "flex", alignItems: "center", marginBottom: "10px", gap: "8px" }}>
                          <strong style={{ fontSize: "15px", color: "#2c3e50" }}>
                            Métricas do Anúncio
                          </strong>

                          <button
                            type="button"
                            className="info-button"
                            title="Clique para entender as métricas"
                            onClick={() => setShowMetricsInfo(true)}
                            style={{
                              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                              color: "white",
                              border: "none",
                              width: "24px",
                              height: "24px",
                              borderRadius: "50%",
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              cursor: "pointer",
                              boxShadow: "0 2px 8px rgba(102, 126, 234, 0.3)"
                            }}
                          >
                            i
                          </button>
                        </div>
                      <div className="card-section metrics">

                        <div className="metric">
                          <span className="value">{impressions}</span>
                          <span className="label">Impressões</span>
                        </div>
                        <div className="metric">
                          <span className="value">{clicks}</span>
                          <span className="label">Cliques</span>
                        </div>
                        <div className="metric">
                          <span className="value">{ctr}</span>
                          <span className="label">CTR</span>
                        </div>
                      </div>

                      <div className="card-section">
                        <p className="muted"><strong>Interesses:</strong></p>
                        <div className="tags">
                          {interestsToShow && Array.isArray(interestsToShow) && interestsToShow.length > 0 ? (
                            interestsToShow.slice(0, 3).map((it, i) => (
                              <span className="tag" key={i}>{typeof it === "string" ? it : it.name || it}</span>
                            ))
                          ) : (
                            <span className="tag more">—</span>
                          )}
                          {interestsToShow && interestsToShow.length > 3 && <span className="tag more">+{interestsToShow.length - 3}</span>}
                        </div>
                      </div>

                      <div className="card-section">
                        <p className="muted"><strong>Locais:</strong></p>
                        <div className="tags">
                          {locationsToShow && Array.isArray(locationsToShow) && locationsToShow.length > 0 ? (
                            locationsToShow.slice(0, 3).map((loc, i) => (
                              <span className="tag" key={i}>{typeof loc === "string" ? loc : loc.name || loc}</span>
                            ))
                          ) : (
                            <span className="tag more">—</span>
                          )}
                          {locationsToShow && locationsToShow.length > 3 && <span className="tag more">+{locationsToShow.length - 3}</span>}
                        </div>
                      </div>
                    </div>

                    <footer className="card-footer">
                      <AppLinkButton onClick={() => handleEdit(ad)} name="editar" />

                      {
                        ad.status === 'PAUSED' || ad.status === 'ACTIVE' || ad.status === 'CAMPAIGN_PAUSED' ? <AppLinkButton onClick={() => handleActive(ad)} name={ad.status === 'CAMPAIGN_PAUSED' ||
                          ad.status === 'PAUSED' ? 'Ativar' : 'Pausar'
                        } /> : <></>
                      }

                      <AppLinkButton onClick={() => handleDelete(ad)} name="excluir" />
                      <AppLinkButton onClick={() => handleView(ad)} name="ver anúncio" />

                    </footer>
                  </article>
                );
              })
            )}
          </div>
        </PullToRefresh>
      </div>
      <FloatingVideoButton isSecondFloatingButton={false} youtubeUrl={"https://www.youtube.com/embed/EhbK4wFubVY"} />

      <ConfirmModal show={showModalDelete}
        title='Excluir'
        isLoading={isLoadingModal}
        confirm={executeDeletion}
        closeModal={() => {
          setShowModalDelete(false)
          setSelectedAnnouncement({})
        }}
      >
        Deseja excluir o anúncio?
      </ConfirmModal>

      <ConfirmModal show={showModalActive}
        title={selectedAnnouncement?.status === 'CAMPAIGN_PAUSED' || selectedAnnouncement?.status === 'PAUSED' ? 'Ativar' : 'Pausar'}
        isLoading={isLoadingModal}
        textBtnConfirm="continuar"
        confirm={executeActiveOrPause}
        closeModal={() => {
          setShowModalActive(false)
          setSelectedAnnouncement({})
        }}
      >
        Deseja <b>{selectedAnnouncement?.status === 'CAMPAIGN_PAUSED' || selectedAnnouncement?.status === 'PAUSED' ? 'ativar' : 'pausar'}</b> o anúncio?
      </ConfirmModal>

      {showMetricsInfo && (
        <InformativeModal
          show={showMetricsInfo}
          closeModal={() => setShowMetricsInfo(false)}
        >
          {buildMetricsInfo()}
        </InformativeModal>
      )}
      {showPreview && (
        <div className="preview-overlay">
          <div className="preview-modal">
            <div className="preview-header">
              <h3>Prévia do anúncio</h3>
              <button
                type="button"
                className="close-btn"
                onClick={() => {
                  setShowPreview(false);
                  setPreviewHtml(null);
                }}
              >
                ✕
              </button>
            </div>

            <div
              className="preview-body"
              // Aqui entra o iframe que veio da Meta
              dangerouslySetInnerHTML={{ __html: previewHtml }}
            />
          </div>
        </div>
      )}
    </div>
  );
}